import React, { useState, useMemo } from "react";
import Label from "components/general/Label"
import SportCode from "components/common/SportCode"
import { getPhaseRSC } from 'helpers/rsc'
import { getCurrentRound } from "components/sports/glf/helper/GLFHelper";
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Button from 'react-bootstrap/Button';
import ButtonToolbar from 'react-bootstrap/ButtonToolbar';
import Stack from 'react-bootstrap/Stack';
import { useUrlFriendlyToParameters } from "helpers/urlFriendly";

function getInitialCurrentHole(participant, currentUnitData) {
  const participantData = currentUnitData?.results?.items?.find(r => r.participant.code === participant);
  const current_hole = participantData?.extendedResults?.find(er => er.er_code === 'CURRENT')?.er_value;
  const complete_hole = participantData?.extendedResults?.find(er => er.er_code === 'COMPLETE')?.er_value;
  return (current_hole)
    ? current_hole
    : (complete_hole)
      ? complete_hole
      : "1"
}

const PlayByPlay = ({ participant, cumulativeData, unitsData }) => {
  const { mirs_rsc } = useUrlFriendlyToParameters()
  let _phase = getPhaseRSC(mirs_rsc);
  const _units = [
    { rsc: _phase + '000100--', round: "1" }
    , { rsc: _phase + '000200--', round: "2" }
    , { rsc: _phase + '000300--', round: "3" }
    , { rsc: _phase + '000400--', round: "4" }
  ]

  let _currentUnit = getCurrentRound(cumulativeData?.cumulativeResults?.progress?.lastUnit, _phase)

  const [currentUnitData, setCurrentUnitData] = useState(unitsData.find(item => item.data?.config?.unit === _currentUnit)?.data);
  const [currentUnit, setCurrentUnit] = useState(_currentUnit);
  const [currentRound, setCurrentRound] = useState(_units.find(u => u.rsc === _currentUnit).round);
  const [currentHole, setCurrentHole] = useState(getInitialCurrentHole(participant, currentUnitData));
  const [currentMetric, setMetric] = useState("metric")

  return (
    <>
      <ButtonToolbar>
        <ButtonGroupRound currentUnit={currentUnit} setCurrentUnit={setCurrentUnit} setCurrentRound={setCurrentRound} setCurrentUnitData={setCurrentUnitData} _units={_units} unitsData={unitsData} />
        <ButtonGroupHole currentHole={currentHole} setCurrentHole={setCurrentHole} currentUnitData={currentUnitData} />
        <ButtonGroupMetric currentMetric={currentMetric} setMetric={setMetric} />
      </ButtonToolbar>

      <Stack className="mt-1 border" direction="horizontal">
        <HoleInfo currentRound={currentRound} currentHole={currentHole} currentUnitData={currentUnitData} />
        <HoleShots participant={participant} currentHole={currentHole} currentUnitData={currentUnitData} currentMetric={currentMetric} />
        <div className="ms-auto"></div>
      </Stack>
    </>
  )
};

export default PlayByPlay;

const HoleInfo = ({ currentRound, currentHole, currentUnitData }) => {
  const holeConfig = currentUnitData?.config?.extendedConfig?.find(hole => hole.code === 'HOLE' && hole.pos === currentHole)
  const par = holeConfig?.value
  return (
    <>
      <Stack direction="vertical" className="align-items-start">
        <div className="p-1">
          <strong><Label data="HeaderRound" /> {currentRound}</strong>
        </div>
        <div className="p-1">
          <strong><Label data="HeaderHole" /> {currentHole} - <Label data="HeaderPar" /> {par}</strong>
        </div>
      </Stack>
    </>
  )
};

const HoleShots = ({ participant, currentHole, currentUnitData, currentMetric }) => {
  return (
    <>
      <Stack direction="vertical" className="align-items-start">
        {
          currentUnitData?.results?.playByPlay
            ?.find(pbp => pbp?.actions.find(a => a.pbpa_period === `${participant}HOLE_${currentHole.padStart(2, '0')}`))
            ?.actions
            .sort(({ pbpa_order: prevOrder }, { pbpa_order: currOrder }) => prevOrder - currOrder)
            .map(action => {
              const shot = action.pbpa_order;
              const action_add = action.pbpa_ActionAdd;
              const from_loc = action?.extendedActions.find(ea => ea.pbpea_code === 'FROM_LOC')?.pbpea_value;
              const from_enh = action?.extendedActions.find(ea => ea.pbpea_code === 'FROM_ENH')?.pbpea_value;
              const fairway_center_m = action?.extendedActions.find(ea => ea.pbpea_code === 'FAIRWAY_CENTER' && ea.pbpea_pos === '0')?.pbpea_value;
              const fairway_center_y = action?.extendedActions.find(ea => ea.pbpea_code === 'FAIRWAY_CENTER' && ea.pbpea_pos === '1')?.pbpea_value;
              const fairway_edge_m = action?.extendedActions.find(ea => ea.pbpea_code === 'FAIRWAY_EDGE' && ea.pbpea_pos === '0')?.pbpea_value;
              const fairway_edge_y = action?.extendedActions.find(ea => ea.pbpea_code === 'FAIRWAY_EDGE' && ea.pbpea_pos === '1')?.pbpea_value;
              const dtp_m = action?.extendedActions.find(ea => ea.pbpea_code === 'DTP' && ea.pbpea_pos === '0')?.pbpea_value;
              const dtp_y = action?.extendedActions.find(ea => ea.pbpea_code === 'DTP' && ea.pbpea_pos === '1')?.pbpea_value;

              return (
                <div key={`pbp-${participant}-${currentHole}-${shot}`}>
                  <Label data="HeaderShot" /> {shot} -
                  {
                    (action_add === "DRP" || action_add === "PTY")
                      ? <SportCode code={action_add} discipline="GLF" entity="@StrokeType" />
                      : (dtp_m === '0.00' || dtp_m === '0')
                        ? <SportCode code="CUP" discipline="GLF" entity="@StrokeLocation" />
                        : (
                          <>
                            {(from_loc) ? (
                              <><SportCode code={from_loc} discipline="GLF" entity="@StrokeLocation" /></>
                            ) : ''}
                            {(from_enh) ? (
                              <>,&nbsp;<SportCode code={from_enh} discipline="GLF" entity="@EnhancedStrokeLocation" /></>
                            ) : ''}
                            {(currentMetric === 'metric')
                              ? (
                                <>
                                  {(fairway_center_m) ? (
                                    <>&nbsp;,{fairway_center_m}&nbsp;<Label data="LabelMeters" />&nbsp;<Label data="LabelFromFairwayCenter" /></>
                                  ) : ''}
                                  {(fairway_edge_m) ? (
                                    <>&nbsp;,{fairway_edge_m}&nbsp;<Label data="LabelMeters" />&nbsp;<Label data="LabelFromFairwayEdge" /></>
                                  ) : ''}
                                  {(dtp_m) ? (
                                    <>&nbsp;,{dtp_m}&nbsp;<Label data="LabelMeters" />&nbsp;<Label data="LabelFromHole" /></>
                                  ) : ''}
                                </>
                              ) : (
                                <>
                                  {(fairway_center_y) ? (
                                    <>&nbsp;{fairway_center_y}&nbsp;<Label data="LabelYards" />&nbsp;<Label data="LabelFromFairwayCenter" /></>
                                  ) : ''}
                                  {(fairway_edge_y) ? (
                                    <>&nbsp;{fairway_edge_y}&nbsp;<Label data="LabelYards" />&nbsp;<Label data="LabelFromFairwayEdge" /></>
                                  ) : ''}
                                  {(dtp_y) ? (
                                    <>&nbsp;{dtp_y}&nbsp;<Label data="LabelYards" />&nbsp;<Label data="LabelFromHole" /></>
                                  ) : ''}
                                </>
                              )
                            }
                          </>
                        )
                  }
                </div>
              )
            })
        }
        <div className="p-1">
        </div>
      </Stack>
    </>
  )
};

const ButtonGroupMetric = ({ currentMetric, setMetric }) => {
  const types = [
    { type: 'metric', label: <Label data="HeaderMetric" /> },
    { type: 'imperial', label: <Label data="HeaderImperial" /> }
  ];

  const handleClick = (e) => {
    setMetric(e.target.value)
  }
  return (
    <ButtonGroup size="sm" className="align-items-end" onClick={handleClick}>
      {types.map((obj, index) => (
        <Button key={`btn-measureunit-${index}`} className="selector" variant="outline-dark" active={currentMetric === obj.type} value={obj.type} >{obj.label}</Button>
      ))}
    </ButtonGroup>
  );
}

const ButtonGroupRound = ({ currentUnit, setCurrentUnit, setCurrentRound, setCurrentUnitData, _units, unitsData }) => {
  const handleClick = (e) => {
    setCurrentUnit(e.target.value)
    setCurrentRound(e.target.innerText)
    setCurrentUnitData(unitsData.find(item => item.data?.config?.unit === e.target.value)?.data)
  }
  return (
    <>
      <Stack direction="vertical" className="align-items-start">
        <div className="text-left"><strong><Label data="HeaderRound" /></strong></div>
        <div>
          <ButtonGroup size="sm" className="me-2" aria-label="Round" onClick={handleClick}>
            {_units.map(unit => {
              const configData = unitsData.find(u => u.data?.config?.unit === unit.rsc)?.data.config
              return (configData) ? (
                <Button key={`btn-round-${unit.round}`} variant="outline-dark" active={currentUnit === unit.rsc} value={unit.rsc} >{unit.round}</Button>
              ) : ''
            })}
          </ButtonGroup>
        </div>
      </Stack>
    </>
  );
}

const ButtonGroupHole = ({ currentHole, setCurrentHole, currentUnitData }) => {
  const handleClick = (e) => {
    setCurrentHole(e.target.value)
  }
  return (
    <>
      <Stack direction="vertical" className="align-items-start">
        <div className="text-left"><strong><Label data="HeaderHole" /></strong></div>
        <div>
          <ButtonGroup size="sm" className="me-2" aria-label="Hole" >
            {
              currentUnitData?.config?.extendedConfig?.filter(res => res.code === 'HOLE').map((hole) => {
                return (
                  <Button key={`btn-hole-${hole.pos}`} active={currentHole === hole.pos} variant="outline-dark" onClick={handleClick} value={hole.pos}>{hole.pos}</Button>
                )
              })
            }
          </ButtonGroup>
        </div>
      </Stack>
    </>
  );
}
